import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  cancelSubscription, 
  getBillingPortalUrl, 
  redirectToBillingPortal,
  selectCurrentPlan, 
  selectSubcriptionLoading,
  leaveSubscription,
  fetchSubscriptionData
} from '../../../../app/store/slices/subscriptionSlice';
import { selectUser } from '../../../../app/store/slices/userSlice';


const ManagePaymentSubscriptionContent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentPlan = useSelector(selectCurrentPlan);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const subscriptionLoading = useSelector(selectSubcriptionLoading);
  const currentUser = useSelector(selectUser);
  const [showConfirmLeave, setShowConfirmLeave] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  const isOwner = currentPlan?.owner===currentUser?._id;
 
  
  //Formate Date : ie June 10 2025
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  //Hanlde Cancel Subscription
  const handleCancelSubscription = async () => {
    setIsCancelling(true);
    try {
      await dispatch(cancelSubscription()).unwrap();
      toast.success('Subscription cancelled successfully');
      dispatch(fetchSubscriptionData())
      setShowConfirmCancel(false);
    } catch (error) {
      toast.error(error.message || 'Failed to cancel subscription');
    } finally {
      setIsCancelling(false);
    }
  };
  //Handle Changing Subscription Plan
  const handlePlanChange = async () => {
    if (!currentPlan?.plan?.planKey) {
      toast.error('Unable to determine current plan');
      return;
    }

    setIsChecking(true);
    try {
      switch (currentPlan.plan.planKey.toLowerCase()) {
        case 'free':
          toast.error('Switch to Other Plans for Smooth Experience', {
            toastId: 'plan-change-free'
          });
          navigate('/user-subscription');
          break;

        case 'single':
          toast.info('You are already on the Single Plan\nWe are working on the Multi-Plan feature', {
            toastId: 'plan-change-single'
          });
          break;

        case 'duo':
          toast.info('You are already on the Duo Plan\nWe are working on the Multi-Plan feature', {
            toastId: 'plan-change-duo'
          });
          break;

        case 'trial':
          toast.info('You are already on the Trial Plan\nSwitch to a Paid Plan', {
            toastId: 'plan-change-trial'
          });
          navigate('/user-subscription');
          break;

        case 'family':
          toast.info('You are already on the Family Plan\nIt is a premium plan', {
            toastId: 'plan-change-family'
          });
          break;

        default:
          toast.info('Unknown plan type', {
            toastId: 'plan-change-unknown'
          });
      }
    } catch (error) {
      console.error('Plan change error:', error);
      toast.error('Failed to process plan change');
    } finally {
      setIsChecking(false);
    }
  };

  //Handle Leave Subscription: For added Members
  const handleLeaveSubscription = async () => {
  setIsLeaving(true);
  try {
    await dispatch(leaveSubscription()).unwrap();
    toast.success('You have left the subscription');
    setShowConfirmLeave(false);

    dispatch(fetchSubscriptionData())
  } catch (error) {
    toast.error(error.message || 'Failed to leave subscription');
  } finally {
    setIsLeaving(false);
  }
};

  //Handle Manage Plan: Redirect to Billing Portal
  const handleManagePlan = async () => {
    try {
      const result = await dispatch(getBillingPortalUrl()).unwrap();
      if (result.url) {
        window.open(result.url, '_blank');
      }
    } catch (error) {
      console.error('Failed to access billing portal:', error);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="p-4">
          <div className="flex justify-center items-center mb-6">
            <button
              onClick={handlePlanChange}
              disabled={isChecking || subscriptionLoading || !currentPlan || !isOwner}
              className="add-btn-settings disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isChecking ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle 
                      className="opacity-25" 
                      cx="12" 
                      cy="12" 
                      r="10" 
                      stroke="currentColor" 
                      strokeWidth="4"
                    />
                    <path 
                      className="opacity-75" 
                      fill="currentColor" 
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Checking Plan...
                </span>
              ) : (
                'Change Plan'
              )}
            </button>
          </div>
          

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-100">
                  <th className="text-left py-3 px-4">Plan</th>
                  <th className="text-left py-3 px-4">Subscription Date</th>
                  <th className="text-left py-3 px-4">Billing Cycle</th>
                  <th className="text-left py-3 px-4">Amount</th>
                  <th className="text-left py-3 px-4">Renewal Date</th>
                  <th className="text-left py-3 px-4">Status</th>
                  <th className="text-left py-3 px-4">Action</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b">
                  <td className="py-3 px-4">{currentPlan?.plan?.name || 'No Plans'}</td>
                  <td className="py-3 px-4">
                    {formatDate(currentPlan?.subscriptionStartDate) || '-'}
                  </td>
                  <td className="py-3 px-4 capitalize">
                    {currentPlan?.billingCycle || 'NA'}
                  </td>
                  <td className="py-3 px-4">
                    ${currentPlan?.plan?.prices[currentPlan?.billingCycle]?.amount}
                  </td>
                  <td className="py-3 px-4">
                    {currentPlan?.nextRenewalDate ? 
                      formatDate(currentPlan.nextRenewalDate) : 
                      '-'
                    }
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded-full text-sm ${
                      currentPlan?.status === 'active' 
                        ? 'bg-green-100 text-green-800'
                        : currentPlan?.status === 'cancelled'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {currentPlan?.status || '-'}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    {currentPlan?.status === 'active' && (
                      isOwner?(
                      <button
                        onClick={() => setShowConfirmCancel(true)}
                        className="text-smg px-4 py-1.5 rounded-lg text-red-600 border border-red-200 hover:bg-red-50"
                      >
                        Cancel
                      </button>
                      
                      ):(
                        <button
                          onClick= {() => setShowConfirmLeave(true)}
                          className="text-smg px-4 py-1.5 rounded-lg text-red-600 border border-red-200 hover:bg-red-50"
                        >
                          Leave
                        </button>
                      )

                    )}
                    {isOwner && (
                      <button
                        onClick={handleManagePlan}
                        disabled={subscriptionLoading}
                        className="px-4 py-1.5 bg-btn-primary ml-4 text-white rounded-lg hover:bg-btn-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                      >
                        {subscriptionLoading ? 'Loading...' : 'Manage Plan'}
                      </button>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Confirmation Modal for Cancel Subscription */}
      {showConfirmCancel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Cancel Subscription</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to cancel your subscription? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowConfirmCancel(false)}
                className="px-4 py-1.5 text-sm border rounded-lg hover:bg-gray-50"
              >
                No, Keep It
              </button>
              <button
                onClick={handleCancelSubscription}
                disabled={isCancelling}
                className="px-4 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-75"
              >
                {isCancelling ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                    Cancelling...
                  </span>
                ) : (
                  'Yes, Cancel'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/**Confirmation Modal for Leave Subscription */}
      {showConfirmLeave && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Leave Subscription</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to leave this subscription? You will lose access to its features.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowConfirmLeave(false)}
                className="px-4 py-1.5 text-sm border rounded-lg hover:bg-gray-50"
              >
                No, Stay
              </button>
              <button
                onClick={handleLeaveSubscription}
                disabled={isLeaving}
                className="px-4 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-75"
              >
                {isLeaving ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                    Leaving...
                  </span>
                ) : (
                  'Yes, Leave'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ManagePaymentSubscriptionContent;